import { useEffect } from 'react';

/**
 * OMOP Session Metadata Event Structure
 *
 * This event is dispatched by the event handler when the backend emits
 * OMOP_SESSION_METADATA events. It contains the session tracking information
 * needed to fetch mapping results.
 */
export interface OmopSessionUpdatedEvent extends CustomEvent {
  detail: {
    session_id: string;
    file_name: string;
    total_codes?: number;
    s3_uri?: string;
    status?: string;
    from_button_click?: boolean;
  };
}

/**
 * Hook to listen for OMOP session updates and trigger dialog opening
 *
 * When the backend mapping agent completes code loading, it emits an
 * OMOP_SESSION_METADATA event. This hook listens for that event and
 * automatically opens the mapping review dialog with the session data.
 *
 * USAGE:
 * ```tsx
 * const { handleOpenDialog } = useMappingReviewDialog();
 * useOmopSessionListener(handleOpenDialog);
 * ```
 *
 * @param onSessionReady - Callback to open dialog with session_id and file_name
 * @param autoOpen - Whether to automatically open dialog (default: true)
 */
export const useOmopSessionListener = (
  onSessionReady: (sessionId: string, fileName: string) => Promise<void>,
  autoOpen = false
): void => {
  useEffect(() => {
    if (!autoOpen) {
      return;
    }

    const handleSessionUpdated = async (event: Event) => {
      const customEvent = event as OmopSessionUpdatedEvent;
      const { session_id, file_name, from_button_click } = customEvent.detail;

      // Only open dialog if this event came from a button click
      if (session_id && file_name && from_button_click) {
        try {
          await onSessionReady(session_id, file_name);
        } catch (error) {
          console.error('Failed to open mapping dialog:', error);
        }
      }
    };

    // Listen for the omop-session-updated event from handleOmopSessionMetadata
    window.addEventListener('omop-session-updated', handleSessionUpdated);

    return () => {
      window.removeEventListener('omop-session-updated', handleSessionUpdated);
    };
  }, [onSessionReady, autoOpen]);
};
